import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import './EditerSite.css';
import { appelAPI } from './api';
// ⚠️ IMPORT PARTAGÉ — doit être IDENTIQUE à celui de PageClient.js.
// Tu as créé configsEtapeSix.js avec `export const configsEtapeSix = {...}`
// (export NOMMÉ) → on l'importe avec les accolades. Si ton fichier
// porte un autre nom, ajuste ce chemin (et celui de PageClient) pour
// qu'ils pointent vers le MÊME fichier.
import { configsEtapeSix } from './configsEtapeSix';
import { moyensPaiement } from './configsPaiement';

// ══════════════════════════════════════════════════════════════
//PEFEP Site — ÉDITEUR DE SITE v2.0
// - Édition des items APRÈS publication, par secteur
// - Utilise la MÊME config de champs que le wizard (configsEtapeSix)
//   → plus de divergence, hérite auto de tous les futurs ajouts
// - École : choisit champsClassique / champsSuperieur selon le type
//   (meta.type_ecole), avec repli intelligent pour les anciens sites
// - Sauvegarde : met à jour contenu_json ET vide html_cache
//   → le site publié reflète la modif immédiatement
// - Route : /mes-sites/:id/editer
// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
// PARSEUR ROBUSTE — Gère tous les formats de contenu_json
// ══════════════════════════════════════════════════════════════
function parseContenu(raw) {
  if (!raw) return null;

  // Cas 1 : Déjà un objet JS
  if (typeof raw === 'object') return raw;

  // Cas 2 : String — nettoyer + parser
  if (typeof raw === 'string') {
    try {
      const clean = raw
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();
      return JSON.parse(clean);
    } catch (e) {
      console.error('❌ Erreur parseContenu :', e);
      return null;
    }
  }

  return null;
}

// ══════════════════════════════════════════════════════════════
// TYPE D'ÉCOLE — résolution pour choisir les bons champs
// Priorité : meta.type_ecole (persisté). Repli anciens sites :
// on infère depuis les champs d'un item existant.
// Renvoie 'superieur' ou 'classique'.
// ══════════════════════════════════════════════════════════════
function resoudreTypeEcole(contenu, items) {
  const brut = contenu?.meta?.type_ecole;
  if (brut === 'Enseignement supérieur') return 'superieur';
  if (brut) return 'classique'; // Kindergarten ou Classique

  // Repli : le type n'a pas été enregistré (site publié avant le Geste B).
  // On devine à partir d'un item : un item supérieur a faculté/diplôme/prérequis.
  const ech = (items && items[0]) || {};
  const cles = { ...ech, ...(ech.details || {}) };
  if (cles.faculte || cles.diplome || cles.prerequis) return 'superieur';
  return 'classique';
}

// ══════════════════════════════════════════════════════════════
// MOYENS DE PAIEMENT — reconstruire l'état d'édition depuis
// contenu.meta.moyens_paiement (pour préremplir la modale).
// ══════════════════════════════════════════════════════════════
function extraireMoyensInitiaux(contenu) {
  const arr = (contenu && contenu.meta && contenu.meta.moyens_paiement) || [];
  const moyensDons = {};
  const virement = {};
  arr.forEach(m => {
    if (!m || !m.id) return;
    if (m.id === 'virement_bancaire') {
      moyensDons.virement_bancaire = '';
      // Les détails du virement sont une chaîne assemblée : on marque
      // seulement le moyen comme actif, le propriétaire réajuste au besoin.
    } else {
      moyensDons[m.id] = m.details || '';
    }
  });
  return { moyensDons, virement };
}

// Upload d'une photo via la route existante, renvoie l'URL.
async function televerserPhoto(fichier, tag, nomOrganisation) {
  const fd = new FormData();
  fd.append('photo', fichier);
  fd.append('tag', tag);
  fd.append('nomClient', nomOrganisation || '');
  const { data } = await appelAPI('/api/upload-photo', { method: 'POST', body: fd });
  if (data && data.succes) return data.url;
  throw new Error((data && data.erreur) || 'Upload de la photo échoué');
}

// ══════════════════════════════════════════════════════════════
// CONFIG UI PAR SECTEUR (libellés de la liste / boutons)
// ══════════════════════════════════════════════════════════════
const CONFIG_SECTEURS = {
  eglise: {
    natureItem: 'culte/ministère',
    natureItemPluriel: 'cultes & ministères',
    titreListe: 'Vos cultes & ministères',
    boutonAjouter: '➕ Ajouter un culte/ministère',
    icone: '⛪',
  },
  restaurant: {
    natureItem: 'plat',
    natureItemPluriel: 'plats',
    titreListe: 'Vos plats',
    boutonAjouter: '➕ Ajouter un plat',
    icone: '🍽️',
  },
  ong: {
    natureItem: 'programme',
    natureItemPluriel: 'programmes',
    titreListe: 'Vos programmes',
    boutonAjouter: '➕ Ajouter un programme',
    icone: '🤝',
  },
  coach: {
    natureItem: 'programme',
    natureItemPluriel: 'programmes',
    titreListe: 'Vos programmes',
    boutonAjouter: '➕ Ajouter un programme',
    icone: '🎯',
  },
  boutique: {
    natureItem: 'produit',
    natureItemPluriel: 'produits',
    titreListe: 'Vos produits',
    boutonAjouter: '➕ Ajouter un produit',
    icone: '🛍️',
  },
  ecole: {
    natureItem: 'formation',
    natureItemPluriel: 'formations',
    titreListe: 'Vos formations',
    boutonAjouter: '➕ Ajouter une formation',
    icone: '🎓',
  },
  salon: {
    natureItem: 'prestation',
    natureItemPluriel: 'prestations',
    titreListe: 'Vos prestations',
    boutonAjouter: '➕ Ajouter une prestation',
    icone: '💅',
  },
  blog: {
    natureItem: 'article',
    natureItemPluriel: 'articles',
    titreListe: 'Vos articles',
    boutonAjouter: '➕ Ajouter un article',
    icone: '✍️',
  },
  immobilier: {
    natureItem: 'bien',
    natureItemPluriel: 'biens',
    titreListe: 'Vos biens',
    boutonAjouter: '➕ Ajouter un bien',
    icone: '🏠',
  },
  freelance: {
    natureItem: 'réalisation',
    natureItemPluriel: 'réalisations',
    titreListe: 'Vos réalisations',
    boutonAjouter: '➕ Ajouter une réalisation',
    icone: '💼',
  },
  liberal: {
    natureItem: 'expertise',
    natureItemPluriel: 'expertises',
    titreListe: 'Vos expertises',
    boutonAjouter: '➕ Ajouter une expertise',
    icone: '⚕️',
  },
};

function getConfigSecteur(secteur) {
  return CONFIG_SECTEURS[secteur] || CONFIG_SECTEURS.freelance;
}

export default function EditerSite() {
  const { id: siteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [site, setSite] = useState(null);
  const [modalOuverte, setModalOuverte] = useState(false);
  const [itemEnEdition, setItemEnEdition] = useState(null);
  const [message, setMessage] = useState('');
  const [modalePaiementOuverte, setModalePaiementOuverte] = useState(false);
  const [panneauOuvert, setPanneauOuvert] = useState(null); // 'infos' | 'accueil' | 'design'

  // ─────────────────────────────────────────────────────────────
  // Charger le site depuis Supabase
  // ─────────────────────────────────────────────────────────────
  const chargerSite = async () => {
    if (!siteId || !user?.id) return;

    try {
      let query = supabase
        .from('sites_generes')
        .select('*')
        .eq('id', siteId);

      // Admin peut éditer TOUS les sites
      if (user?.role !== 'admin') {
        query = query.eq('client_id', user.id);
      }

      const { data, error } = await query.single();

      if (error || !data) {
        setErreur('Impossible de charger ce site.');
        return;
      }

      setSite(data);
    } catch (err) {
      setErreur('Erreur : ' + err.message);
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    chargerSite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, user]);

  // ─────────────────────────────────────────────────────────────
  // Contenu parsé une seule fois (items + meta)
  // ─────────────────────────────────────────────────────────────
  const contenu = site ? parseContenu(site.contenu_json) : null;

  const getItems = () => {
    if (!contenu) return [];
    return (
      contenu?.page_section_principale?.items ||
      contenu?.page_services?.items ||
      contenu?.page_menu?.items ||
      contenu?.page_produits?.items ||
      contenu?.page_programmes?.items ||
      contenu?.items ||
      []
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Sauvegarder les items → contenu_json + VIDER html_cache
  // ─────────────────────────────────────────────────────────────
  const sauvegarderItems = async (nouveauxItems, typeEcoleLabel) => {
    if (!site) return { succes: false };

    let contenuMisAJour = parseContenu(site.contenu_json);
    if (!contenuMisAJour) {
      return { succes: false, erreur: 'Impossible de parser le contenu du site' };
    }

    // Cloner pour ne pas muter l'état
    contenuMisAJour = JSON.parse(JSON.stringify(contenuMisAJour));

    // Mémoriser le type d'école choisi → l'éditeur rouvrira sur le bon
    // type, et ça reste cohérent partout.
    if (typeEcoleLabel) {
      contenuMisAJour.meta = contenuMisAJour.meta || {};
      contenuMisAJour.meta.type_ecole = typeEcoleLabel;
    }

    // Mettre à jour les items — plusieurs emplacements possibles
    if (contenuMisAJour.page_section_principale) {
      contenuMisAJour.page_section_principale.items = nouveauxItems;
    } else if (contenuMisAJour.page_services) {
      contenuMisAJour.page_services.items = nouveauxItems;
    } else if (contenuMisAJour.page_menu) {
      contenuMisAJour.page_menu.items = nouveauxItems;
    } else if (contenuMisAJour.page_produits) {
      contenuMisAJour.page_produits.items = nouveauxItems;
    } else if (contenuMisAJour.page_programmes) {
      contenuMisAJour.page_programmes.items = nouveauxItems;
    } else {
      contenuMisAJour.page_section_principale = { items: nouveauxItems };
    }

    // Mettre à jour l'aperçu accueil (min 3, max 6)
    const nbApercu = Math.min(6, Math.max(3, Math.ceil(nouveauxItems.length / 2)));
    const apercu = nouveauxItems.slice(0, nbApercu).map(item => ({
      titre: item.titre,
      sous_titre: item.sous_titre || '',
      description_courte: (item.description || '').substring(0, 100),
      prix: item.prix || item.scolarite || item.frais || item.tarif || '',
      photo_url: item.photo_url || item.photo || '',
    }));

    if (contenuMisAJour.page_accueil) {
      contenuMisAJour.page_accueil.apercu_items = apercu;
    }

    // ⭐ Sauvegarde : contenu_json À JOUR + html_cache vidé.
    // Vider le cache force le site publié à se régénérer à la prochaine
    // visite avec le nouveau contenu — sans quoi la modif resterait
    // invisible en ligne.
    const { error } = await supabase
      .from('sites_generes')
      .update({
        contenu_json: contenuMisAJour,
        html_cache: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', siteId);

    if (error) {
      return { succes: false, erreur: error.message };
    }

    await chargerSite();
    return { succes: true };
  };

  // ─────────────────────────────────────────────────────────────
  // Ajouter ou modifier un item
  // ─────────────────────────────────────────────────────────────
  const handleSauvegarderItem = async (nouvelItem, typeEcoleLabel) => {
    const items = getItems();
    let nouveauxItems;

    if (itemEnEdition !== null) {
      nouveauxItems = items.map((item, i) => (i === itemEnEdition ? nouvelItem : item));
    } else {
      nouveauxItems = [...items, nouvelItem];
    }

    const result = await sauvegarderItems(nouveauxItems, typeEcoleLabel);
    if (result.succes) {
      setModalOuverte(false);
      setItemEnEdition(null);
      setMessage(itemEnEdition !== null ? '✅ Modification enregistrée !' : '✅ Nouvel élément ajouté !');
      setTimeout(() => setMessage(''), 3000);
    } else {
      alert('Erreur : ' + result.erreur);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Supprimer un item
  // ─────────────────────────────────────────────────────────────
  const handleSupprimerItem = async (index) => {
    const items = getItems();
    if (items.length <= 3) {
      alert(`Impossible de supprimer : il faut au moins 3 ${getConfigSecteur(site.secteur).natureItemPluriel} sur votre site.`);
      return;
    }

    if (!window.confirm(`Voulez-vous vraiment supprimer "${items[index].titre}" ?`)) return;

    const nouveauxItems = items.filter((_, i) => i !== index);
    const result = await sauvegarderItems(nouveauxItems);

    if (result.succes) {
      setMessage('✅ Élément supprimé !');
      setTimeout(() => setMessage(''), 3000);
    } else {
      alert('Erreur : ' + result.erreur);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Sauvegarder les moyens de paiement → meta.moyens_paiement + cache
  // ─────────────────────────────────────────────────────────────
  const sauvegarderPaiements = async (formatted) => {
    let c = parseContenu(site.contenu_json);
    if (!c) { alert('Impossible de parser le contenu du site'); return; }
    c = JSON.parse(JSON.stringify(c));
    c.meta = c.meta || {};
    c.meta.moyens_paiement = formatted;

    const { error } = await supabase
      .from('sites_generes')
      .update({ contenu_json: c, html_cache: null, updated_at: new Date().toISOString() })
      .eq('id', siteId);

    if (error) { alert('Erreur : ' + error.message); return; }

    await chargerSite();
    setModalePaiementOuverte(false);
    setMessage('✅ Moyens de paiement enregistrés !');
    setTimeout(() => setMessage(''), 3000);
  };

  // ─────────────────────────────────────────────────────────────
  // Sauvegarde générique d'une ou plusieurs colonnes (contenu_json /
  // design_json / photos_json) + vidage du cache. Utilisée par les
  // panneaux Infos, Accueil et Design. 0 token, écriture directe.
  // ─────────────────────────────────────────────────────────────
  const sauvegarderColonnes = async (colonnes, messageOk) => {
    const { error } = await supabase
      .from('sites_generes')
      .update({ ...colonnes, html_cache: null, updated_at: new Date().toISOString() })
      .eq('id', siteId);
    if (error) { alert('Erreur : ' + error.message); return false; }
    await chargerSite();
    setPanneauOuvert(null);
    setMessage(messageOk || '✅ Enregistré !');
    setTimeout(() => setMessage(''), 3000);
    return true;
  };

  // ─────────────────────────────────────────────────────────────
  // AFFICHAGE — Chargement
  // ─────────────────────────────────────────────────────────────
  if (chargement) {
    return (
      <div className="edit-loading">
        <div className="edit-spinner"></div>
        <p>Chargement de votre site...</p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // AFFICHAGE — Erreur
  // ─────────────────────────────────────────────────────────────
  if (erreur || !site) {
    return (
      <div className="edit-erreur">
        <div className="edit-erreur-icone">⚠️</div>
        <h2>Une erreur est survenue</h2>
        <p>{erreur || 'Site introuvable'}</p>
        <button type="button" className="edit-btn-retour" onClick={() => navigate('/tableau-de-bord')}>
          ← Retour au tableau de bord
        </button>
      </div>
    );
  }

  const config = getConfigSecteur(site.secteur);
  const items = getItems();
  const typeEcole = resoudreTypeEcole(contenu, items);
  const typeEcoleLabel = (contenu && contenu.meta && contenu.meta.type_ecole)
    || (typeEcole === 'superieur' ? 'Enseignement supérieur' : 'Classique (Primaire / Secondaire)');
  const moyensInitiaux = extraireMoyensInitiaux(contenu);
  const design = (site.design_json && typeof site.design_json === 'object') ? site.design_json : {};
  const photos = (site.photos_json && typeof site.photos_json === 'object') ? site.photos_json : {};

  return (
    <div className="edit-container">
      {/* Barre supérieure */}
      <div className="edit-topbar">
        <button type="button" className="edit-btn-back" onClick={() => navigate('/tableau-de-bord')}>
          ← Retour au tableau de bord
        </button>
        <div className="edit-topbar-info">
          <span className="edit-topbar-nom">{config.icone} {site.nom_organisation}</span>
          <span className="edit-topbar-badge">✏ Édition</span>
        </div>
      </div>

      {/* Message flash */}
      {message && <div className="edit-flash">{message}</div>}

      {/* Contenu principal */}
      <div className="edit-content">
        <div className="edit-info-box">
          <div className="edit-info-icone">💡</div>
          <div>
            <strong>Astuce :</strong> Vous pouvez ajouter autant de {config.natureItemPluriel} que vous voulez, <strong>gratuitement et illimité</strong> ! Vos modifications apparaissent sur votre site en ligne.
          </div>
        </div>

        <div className="edit-section-header">
          <h2>{config.icone} {config.titreListe} ({items.length})</h2>
          <button
            type="button"
            className="edit-btn-ajouter"
            onClick={() => {
              setItemEnEdition(null);
              setModalOuverte(true);
            }}
          >
            {config.boutonAjouter}
          </button>
        </div>

        {items.length === 0 ? (
          <div className="edit-empty">
            <div className="edit-empty-icone">{config.icone}</div>
            <h3>Aucun {config.natureItem} pour le moment</h3>
            <p>Commencez par ajouter votre premier {config.natureItem} !</p>
          </div>
        ) : (
          <div className="edit-items-liste">
            {items.map((item, index) => (
              <div key={index} className="edit-item-card">
                <div className="edit-item-photo">
                  {(item.photo_url || item.photo) ? (
                    <img src={item.photo_url || item.photo} alt={item.titre} />
                  ) : (
                    <div className="edit-item-photo-vide">{config.icone}</div>
                  )}
                </div>
                <div className="edit-item-info">
                  {item.sous_titre && <span className="edit-item-categorie">{item.sous_titre}</span>}
                  <h3>{item.titre}</h3>
                  {item.description && <p className="edit-item-description">{item.description}</p>}
                  {(item.prix || item.scolarite || item.frais || item.tarif) && (
                    <div className="edit-item-prix">{item.prix || item.scolarite || item.frais || item.tarif}</div>
                  )}
                </div>
                <div className="edit-item-actions">
                  <button
                    type="button"
                    className="edit-item-btn edit-item-btn-modifier"
                    onClick={() => {
                      setItemEnEdition(index);
                      setModalOuverte(true);
                    }}
                  >
                    ✏ Modifier
                  </button>
                  <button
                    type="button"
                    className="edit-item-btn edit-item-btn-supprimer"
                    onClick={() => handleSupprimerItem(index)}
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="edit-footer-actions">
          <button type="button" className="edit-btn-section" onClick={() => setPanneauOuvert('infos')}>
            ✏ Infos de base
          </button>
          <button type="button" className="edit-btn-section" onClick={() => setPanneauOuvert('accueil')}>
            🏠 Page d'accueil
          </button>
          <button type="button" className="edit-btn-section" onClick={() => setPanneauOuvert('design')}>
            🎨 Design
          </button>
          <button
            type="button"
            className="edit-btn-paiement"
            onClick={() => setModalePaiementOuverte(true)}
          >
            💳 Moyens de paiement
          </button>
          <button
            type="button"
            className="edit-btn-voir"
            onClick={() => navigate(`/mes-sites/${siteId}`)}
          >
            👁 Voir mon site à jour
          </button>
        </div>
      </div>

      {modalOuverte && (
        <ModalItem
          item={itemEnEdition !== null ? items[itemEnEdition] : null}
          config={config}
          secteur={site.secteur}
          typeEcole={typeEcole}
          typeEcoleLabel={typeEcoleLabel}
          nomOrganisation={site.nom_organisation}
          onSauvegarder={handleSauvegarderItem}
          onFermer={() => {
            setModalOuverte(false);
            setItemEnEdition(null);
          }}
        />
      )}

      {modalePaiementOuverte && (
        <ModalPaiement
          moyensInitiaux={moyensInitiaux}
          onSauvegarder={sauvegarderPaiements}
          onFermer={() => setModalePaiementOuverte(false)}
        />
      )}

      {panneauOuvert === 'infos' && (
        <ModalInfos
          contenu={contenu}
          photos={photos}
          nomOrganisation={site.nom_organisation}
          onSauvegarder={sauvegarderColonnes}
          onFermer={() => setPanneauOuvert(null)}
        />
      )}

      {panneauOuvert === 'accueil' && (
        <ModalAccueil
          contenu={contenu}
          photos={photos}
          nomOrganisation={site.nom_organisation}
          onSauvegarder={sauvegarderColonnes}
          onFermer={() => setPanneauOuvert(null)}
        />
      )}

      {panneauOuvert === 'design' && (
        <ModalDesign
          design={design}
          onSauvegarder={sauvegarderColonnes}
          onFermer={() => setPanneauOuvert(null)}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// COMPOSANT — Modal d'ajout/modification d'un item
// Les champs affichés viennent de la MÊME config que le wizard
// (configsEtapeSix). Pour l'École, on choisit champsClassique /
// champsSuperieur selon le type d'école.
// ══════════════════════════════════════════════════════════════
function ModalItem({ item, config, secteur, typeEcole, typeEcoleLabel, nomOrganisation, onSauvegarder, onFermer }) {
  // Garde-fou : si l'import de config a échoué, on ne plante pas —
  // on affiche un message clair plutôt qu'un écran blanc.
  const configChamps = (configsEtapeSix && configsEtapeSix[secteur])
    || (configsEtapeSix && configsEtapeSix.freelance)
    || null;

  // École : comme à la CRÉATION, on choisit le type d'établissement et
  // les champs s'adaptent (Kindergarten/Classique vs Enseignement supérieur).
  const OPTIONS_ECOLE = ['Kindergarten', 'Classique (Primaire / Secondaire)', 'Enseignement supérieur'];
  const [typeEcoleSel, setTypeEcoleSel] = useState(
    secteur === 'ecole'
      ? (typeEcoleLabel || (typeEcole === 'superieur' ? 'Enseignement supérieur' : 'Classique (Primaire / Secondaire)'))
      : ''
  );
  const estSuperieur = typeEcoleSel === 'Enseignement supérieur';

  // Sélection des champs : École = classique/supérieur selon le choix, sinon `champs`.
  let champs = [];
  if (configChamps) {
    if (secteur === 'ecole') {
      champs = estSuperieur
        ? (configChamps.champsSuperieur || [])
        : (configChamps.champsClassique || []);
    } else {
      champs = configChamps.champs || [];
    }
  }

  const labelItem = (configChamps && configChamps.labelItem) || config.natureItem || 'élément';
  const aUnPrix = champs.some(c => c.id === 'prix' || c.id === 'tarif');

  // formData initialisé depuis l'item existant OU vide, pour CHAQUE champ
  const initial = {};
  champs.forEach(c => {
    const existant = item
      ? (item[c.id] !== undefined ? item[c.id] : (item.details && item.details[c.id]))
      : undefined;
    initial[c.id] = existant !== undefined && existant !== null
      ? existant
      : (c.type === 'multi' ? [] : '');
  });
  if (item && item.photo_url && !initial.photo) initial.photo = item.photo_url;

  const [formData, setFormData] = useState(initial);
  const [uploadEnCours, setUploadEnCours] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreurUpload, setErreurUpload] = useState('');

  const modeModification = item !== null;

  const updateChamp = (champ, valeur) => {
    setFormData(prev => ({ ...prev, [champ]: valeur }));
  };

  const handleUploadPhoto = async (champId, fichier) => {
    if (!fichier) return;
    setUploadEnCours(true);
    setErreurUpload('');
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('photo', fichier);
      formDataUpload.append('tag', 'item');
      formDataUpload.append('nomClient', nomOrganisation);

      const { data } = await appelAPI('/api/upload-photo', {
        method: 'POST',
        body: formDataUpload,
      });

      if (data.succes) {
        updateChamp(champId, data.url);
      } else {
        setErreurUpload(data.erreur || 'Erreur lors de l\'upload');
      }
    } catch (err) {
      setErreurUpload('Impossible de contacter le serveur');
    } finally {
      setUploadEnCours(false);
    }
  };

  // Le champ titre = 'nom' ou 'titre', sinon le premier champ
  const champTitreId = (champs.find(c => c.id === 'nom' || c.id === 'titre') || champs[0] || { id: 'nom' }).id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const titreValeur = String(formData[champTitreId] || '').trim();
    if (!titreValeur) {
      alert('Le titre est obligatoire');
      return;
    }
    setEnregistrement(true);

    // On structure l'item comme à la création : un titre, UNE description
    // (le paragraphe), un prix, et des "details" avec de VRAIS LIBELLÉS
    // (pas les identifiants techniques). La description ne va PAS dans
    // details (sinon elle s'affiche en double) ; le montant + la
    // périodicité fusionnent dans le prix.
    const nettoieLabel = (l) => String(l || '').replace(/\s*\*\s*$/, '').trim();
    const champPrix = champs.find(c => ['prix', 'tarif', 'scolarite', 'frais'].includes(c.id));
    const aPeriodicite = champs.some(c => c.id === 'periodicite');

    const exclusDetails = new Set([champTitreId, 'photo', 'description', 'description_courte', 'periodicite']);
    if (champPrix) exclusDetails.add(champPrix.id);

    const details = {};
    champs.forEach(c => {
      if (exclusDetails.has(c.id)) return;
      const v = formData[c.id];
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return;
      details[nettoieLabel(c.label)] = Array.isArray(v) ? v.join(', ') : v;
    });

    // Prix = montant (+ périodicité si ce n'est pas "Unique")
    let prix = formData.prix || formData.tarif || '';
    if (champPrix) {
      const montant = String(formData[champPrix.id] || '').trim();
      if (montant) {
        const per = aPeriodicite ? String(formData.periodicite || '').trim() : '';
        prix = (per && per.toLowerCase() !== 'unique')
          ? montant + ' / ' + per.replace(/^par\s+/i, '')
          : montant;
      }
    }

    const itemFinal = {
      ...formData,
      titre: titreValeur,
      nom: titreValeur,
      description: String(formData.description || formData.description_courte || '').trim(),
      prix,
      photo_url: formData.photo || formData.photo_url || '',
      details,
    };

    // Action « commander » pour les secteurs qui vendent (le libellé du
    // bouton reste dérivé du secteur par le générateur).
    if (aUnPrix) itemFinal.cta_action = 'commander';

    await onSauvegarder(itemFinal, secteur === 'ecole' ? typeEcoleSel : null);
    setEnregistrement(false);
  };

  return (
    <div className="edit-modal-overlay" onClick={onFermer}>
      <div className="edit-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="edit-modal-close" onClick={onFermer}>×</button>

        <div className="edit-modal-header">
          <h2>{modeModification ? `✏ Modifier ${labelItem}` : `➕ Ajouter ${labelItem}`}</h2>
        </div>

        {champs.length === 0 ? (
          <div className="edit-modal-body">
            <div className="edit-modal-erreur">
              Les champs de ce secteur n'ont pas pu être chargés. Vérifiez que
              le fichier <strong>configsEtapeSix.js</strong> est bien importé.
            </div>
          </div>
        ) : (
          <form className="edit-modal-body" onSubmit={handleSubmit}>
            {secteur === 'ecole' && (
              <div className="edit-modal-champ">
                <label>Type d'établissement</label>
                <div className="edit-choix-groupe">
                  {OPTIONS_ECOLE.map(opt => (
                    <button
                      type="button"
                      key={opt}
                      className={`edit-choix-btn ${typeEcoleSel === opt ? 'actif' : ''}`}
                      onClick={() => setTypeEcoleSel(opt)}
                    >
                      {typeEcoleSel === opt ? '✅ ' : ''}{opt}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {champs.map(champ => {
              // Filtrage conditionnel (ex: lien online seulement si mode online)
              if (champ.conditionalOn) {
                const valeurCond = formData[champ.conditionalOn.field];
                if (!champ.conditionalOn.values.includes(valeurCond)) return null;
              }

              if (champ.type === 'photo') {
                return (
                  <div className="edit-modal-champ" key={champ.id}>
                    <label>📸 {champ.label}</label>
                    <div className="edit-modal-photo-zone">
                      {formData[champ.id] ? (
                        <div className="edit-modal-photo-preview">
                          <img src={formData[champ.id]} alt="Aperçu" />
                          <button
                            type="button"
                            className="edit-modal-photo-supprimer"
                            onClick={() => updateChamp(champ.id, '')}
                          >
                            ✕ Retirer
                          </button>
                        </div>
                      ) : (
                        <div className="edit-modal-photo-upload">
                          <input
                            type="file"
                            id={`photo-upload-${champ.id}`}
                            accept="image/*"
                            onChange={e => handleUploadPhoto(champ.id, e.target.files[0])}
                            disabled={uploadEnCours}
                          />
                          <label htmlFor={`photo-upload-${champ.id}`} className="edit-modal-photo-btn">
                            {uploadEnCours ? '⏳ Upload en cours...' : '📤 Choisir une photo'}
                          </label>
                          <p className="edit-modal-photo-info">Max 5 MB — JPG, PNG</p>
                        </div>
                      )}
                    </div>
                    {erreurUpload && <div className="edit-modal-erreur">{erreurUpload}</div>}
                  </div>
                );
              }

              if (champ.type === 'choix') {
                return (
                  <div className="edit-modal-champ" key={champ.id}>
                    <label>{champ.label}</label>
                    <div className="edit-choix-groupe">
                      {champ.options.map(opt => (
                        <button
                          type="button"
                          key={opt}
                          className={`edit-choix-btn ${formData[champ.id] === opt ? 'actif' : ''}`}
                          onClick={() => updateChamp(champ.id, opt)}
                        >
                          {formData[champ.id] === opt ? '✅ ' : ''}{opt}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              if (champ.type === 'multi') {
                const selection = Array.isArray(formData[champ.id]) ? formData[champ.id] : [];
                const basculer = (opt) => {
                  const suite = selection.includes(opt)
                    ? selection.filter(o => o !== opt)
                    : [...selection, opt];
                  updateChamp(champ.id, suite);
                };
                return (
                  <div className="edit-modal-champ" key={champ.id}>
                    <label>{champ.label}</label>
                    <div className="edit-choix-groupe">
                      {champ.options.map(opt => (
                        <button
                          type="button"
                          key={opt}
                          className={`edit-choix-btn ${selection.includes(opt) ? 'actif' : ''}`}
                          onClick={() => basculer(opt)}
                        >
                          {selection.includes(opt) ? '✅ ' : ''}{opt}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              if (champ.type === 'textarea') {
                return (
                  <div className="edit-modal-champ" key={champ.id}>
                    <label>{champ.label}</label>
                    <textarea
                      placeholder={champ.placeholder || ''}
                      value={formData[champ.id] || ''}
                      onChange={e => updateChamp(champ.id, e.target.value)}
                      rows={4}
                    />
                  </div>
                );
              }

              // type 'text' par défaut
              return (
                <div className="edit-modal-champ" key={champ.id}>
                  <label>{champ.label}</label>
                  <input
                    type="text"
                    placeholder={champ.placeholder || ''}
                    value={formData[champ.id] || ''}
                    onChange={e => updateChamp(champ.id, e.target.value)}
                  />
                </div>
              );
            })}

            <button
              type="submit"
              className="edit-modal-btn-submit"
              disabled={enregistrement || uploadEnCours}
            >
              {enregistrement ? '⏳ Enregistrement...' : (modeModification ? '✅ Enregistrer les modifications' : `✅ Ajouter ${labelItem}`)}
            </button>

            <div className="edit-modal-mention">
              <span className="edit-obligatoire">*</span> Champ obligatoire
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// COMPOSANT — Modal des MOYENS DE PAIEMENT (édition post-publication)
// Écrit contenu.meta.moyens_paiement. Le générateur les affiche déjà
// dans la modale de commande des sites → débloque les sites publiés.
// ══════════════════════════════════════════════════════════════
function ModalPaiement({ moyensInitiaux, onSauvegarder, onFermer }) {
  const [moyensDons, setMoyensDons] = useState(moyensInitiaux.moyensDons || {});
  const [virement, setVirement] = useState(moyensInitiaux.virement || {});
  const [enreg, setEnreg] = useState(false);

  const estActif = (id) => moyensDons[id] !== undefined;
  const toggle = (id) => {
    setMoyensDons(prev => {
      const copie = { ...prev };
      if (copie[id] !== undefined) delete copie[id];
      else copie[id] = '';
      return copie;
    });
  };
  const setValeur = (id, val) => setMoyensDons(prev => ({ ...prev, [id]: val }));
  const setVir = (champ, val) => setVirement(prev => ({ ...prev, [champ]: val }));

  const handleSubmit = async () => {
    setEnreg(true);
    const formatted = moyensPaiement
      .filter(opt => estActif(opt.id))
      .map(opt => {
        if (opt.id === 'virement_bancaire') {
          const det = virement.banque
            ? `${virement.banque} — ${virement.titulaire || ''} — ${virement.numero || ''}${virement.monnaie ? ' (' + virement.monnaie + ')' : ''}`
            : 'Détails communiqués après la commande';
          return { id: 'virement_bancaire', label: 'Virement bancaire', details: det };
        }
        return { id: opt.id, label: opt.label, details: moyensDons[opt.id] || '' };
      });
    await onSauvegarder(formatted);
    setEnreg(false);
  };

  return (
    <div className="edit-modal-overlay" onClick={onFermer}>
      <div className="edit-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="edit-modal-close" onClick={onFermer}>×</button>
        <div className="edit-modal-header">
          <h2>💳 Moyens de paiement</h2>
        </div>
        <div className="edit-modal-body">
          <p style={{ color: '#5a6672', marginTop: 0 }}>
            Activez les moyens que vous acceptez. Ils s'afficheront au moment
            de la commande, et le client choisira celui qu'il préfère.
          </p>

          <div className="edit-choix-groupe">
            {moyensPaiement.map(opt => (
              <button
                type="button"
                key={opt.id}
                className={`edit-choix-btn ${estActif(opt.id) ? 'actif' : ''}`}
                onClick={() => toggle(opt.id)}
              >
                {estActif(opt.id) ? '✅ ' : ''}{opt.label}
              </button>
            ))}
          </div>

          {moyensPaiement.filter(opt => estActif(opt.id) && !opt.virementDetaille).map(opt => (
            <div className="edit-modal-champ" key={opt.id}>
              <label>{opt.champ}</label>
              <input
                type="text"
                placeholder={opt.placeholder || ''}
                value={moyensDons[opt.id] || ''}
                onChange={e => setValeur(opt.id, e.target.value)}
              />
            </div>
          ))}

          {estActif('virement_bancaire') && (
            <div className="edit-virement-block">
              <div className="edit-modal-champ">
                <label>Titulaire</label>
                <input type="text" placeholder="Ex: Isaac PRESMY" value={virement.titulaire || ''} onChange={e => setVir('titulaire', e.target.value)} />
              </div>
              <div className="edit-modal-champ">
                <label>Numéro de compte</label>
                <input type="text" placeholder="Ex: 1234567890" value={virement.numero || ''} onChange={e => setVir('numero', e.target.value)} />
              </div>
              <div className="edit-modal-champ">
                <label>Banque</label>
                <input type="text" placeholder="Ex: Sogebank" value={virement.banque || ''} onChange={e => setVir('banque', e.target.value)} />
              </div>
              <div className="edit-modal-champ">
                <label>Monnaie</label>
                <div className="edit-choix-groupe">
                  {['Gourdes (HTG)', 'Dollars (USD)'].map(m => (
                    <button type="button" key={m} className={`edit-choix-btn ${virement.monnaie === m ? 'actif' : ''}`} onClick={() => setVir('monnaie', m)}>
                      {virement.monnaie === m ? '✅ ' : ''}{m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <button type="button" className="edit-modal-btn-submit" disabled={enreg} onClick={handleSubmit}>
            {enreg ? '⏳ Enregistrement...' : '✅ Enregistrer les moyens de paiement'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PANNEAU — Informations de base (nom, contact, logo, réseaux)
// Écrit contenu.meta + contenu.page_contact + photos_json.logo
// ══════════════════════════════════════════════════════════════
function ModalInfos({ contenu, photos, nomOrganisation, onSauvegarder, onFermer }) {
  const meta = (contenu && contenu.meta) || {};
  const contact = (contenu && contenu.page_contact) || {};
  const reseaux = meta.reseaux_sociaux || {};
  const RESEAUX = ['Facebook', 'Instagram', 'WhatsApp', 'TikTok', 'YouTube', 'LinkedIn'];

  const [nom, setNom] = useState(meta.nom_organisation || nomOrganisation || '');
  const [slogan, setSlogan] = useState(meta.slogan || '');
  const [email, setEmail] = useState(contact.email || '');
  const [whatsapp, setWhatsapp] = useState(contact.whatsapp || '');
  const [adresse, setAdresse] = useState(contact.adresse || '');
  const [logo, setLogo] = useState(meta.logo_url || (photos && photos.logo) || '');
  const [liens, setLiens] = useState(() => {
    const o = {};
    RESEAUX.forEach(r => { if (reseaux[r]) o[r] = reseaux[r]; });
    return o;
  });
  const [enreg, setEnreg] = useState(false);
  const [upl, setUpl] = useState(false);

  const majLien = (r, v) => setLiens(prev => {
    const c = { ...prev };
    if (v && v.trim()) c[r] = v; else delete c[r];
    return c;
  });

  const handleLogo = async (f) => {
    if (!f) return;
    setUpl(true);
    try { const url = await televerserPhoto(f, 'logo', nom); setLogo(url); }
    catch (e) { alert(e.message); }
    finally { setUpl(false); }
  };

  const handleSubmit = async () => {
    if (!nom.trim()) { alert('Le nom est obligatoire'); return; }
    setEnreg(true);
    const c = JSON.parse(JSON.stringify(contenu || {}));
    c.meta = c.meta || {};
    c.meta.nom_organisation = nom.trim();
    c.meta.slogan = slogan.trim();
    c.meta.logo_url = logo || '';
    c.meta.reseaux_sociaux = liens;
    c.page_contact = c.page_contact || {};
    c.page_contact.email = email.trim();
    c.page_contact.whatsapp = whatsapp.trim();
    c.page_contact.adresse = adresse.trim();
    const ph = (photos && typeof photos === 'object') ? { ...photos } : {};
    ph.logo = logo || '';
    await onSauvegarder({ contenu_json: c, photos_json: ph }, '✅ Informations enregistrées !');
    setEnreg(false);
  };

  return (
    <div className="edit-modal-overlay" onClick={onFermer}>
      <div className="edit-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="edit-modal-close" onClick={onFermer}>×</button>
        <div className="edit-modal-header"><h2>✏ Informations de base</h2></div>
        <div className="edit-modal-body">
          <div className="edit-modal-champ">
            <label>Nom de l'organisation *</label>
            <input type="text" value={nom} onChange={e => setNom(e.target.value)} placeholder="Ex: PEFEP Resto Bar" />
          </div>
          <div className="edit-modal-champ">
            <label>Slogan</label>
            <input type="text" value={slogan} onChange={e => setSlogan(e.target.value)} placeholder="Ex: Le goût authentique" />
          </div>
          <div className="edit-modal-champ">
            <label>Email de contact</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@..." />
            <p className="edit-modal-photo-info">Cet e-mail reçoit les commandes/inscriptions du site.</p>
          </div>
          <div className="edit-modal-champ">
            <label>WhatsApp</label>
            <input type="text" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="+509 0000 0000" />
          </div>
          <div className="edit-modal-champ">
            <label>Adresse</label>
            <input type="text" value={adresse} onChange={e => setAdresse(e.target.value)} placeholder="Ex: Léogâne" />
          </div>

          <div className="edit-modal-champ">
            <label>📸 Logo</label>
            <div className="edit-modal-photo-zone">
              {logo ? (
                <div className="edit-modal-photo-preview">
                  <img src={logo} alt="Logo" />
                  <button type="button" className="edit-modal-photo-supprimer" onClick={() => setLogo('')}>✕ Retirer</button>
                </div>
              ) : (
                <div className="edit-modal-photo-upload">
                  <input type="file" id="upload-logo" accept="image/*" onChange={e => handleLogo(e.target.files[0])} disabled={upl} />
                  <label htmlFor="upload-logo" className="edit-modal-photo-btn">{upl ? '⏳ Upload...' : '📤 Choisir un logo'}</label>
                  <p className="edit-modal-photo-info">Max 5 MB — JPG, PNG</p>
                </div>
              )}
            </div>
          </div>

          <div className="edit-modal-champ">
            <label>Réseaux sociaux</label>
            {RESEAUX.map(r => (
              <div className="edit-reseau-ligne" key={r}>
                <span className="edit-reseau-label">{r}</span>
                <input type="url" value={liens[r] || ''} onChange={e => majLien(r, e.target.value)} placeholder={`Lien ${r} (optionnel)`} />
              </div>
            ))}
          </div>

          <button type="button" className="edit-modal-btn-submit" disabled={enreg || upl} onClick={handleSubmit}>
            {enreg ? '⏳ Enregistrement...' : '✅ Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PANNEAU — Page d'accueil (bannière/hero, à propos, invitation)
// Écrit contenu.page_accueil.hero + contenu.page_a_propos.histoire
// + contenu.page_accueil.message_contact + photos_json.hero
// ══════════════════════════════════════════════════════════════
function ModalAccueil({ contenu, photos, nomOrganisation, onSauvegarder, onFermer }) {
  const acc = (contenu && contenu.page_accueil) || {};
  const hero = acc.hero || {};
  const apropos = (contenu && contenu.page_a_propos) || {};

  const [titre, setTitre] = useState(hero.titre || '');
  const [sousTitre, setSousTitre] = useState(hero.sous_titre || '');
  const [image, setImage] = useState(hero.photo_url || (photos && photos.hero) || '');
  const [histoire, setHistoire] = useState(apropos.histoire || '');
  const [invitation, setInvitation] = useState(acc.message_contact || '');
  const [enreg, setEnreg] = useState(false);
  const [upl, setUpl] = useState(false);

  const handleImg = async (f) => {
    if (!f) return;
    setUpl(true);
    try { const url = await televerserPhoto(f, 'hero', nomOrganisation); setImage(url); }
    catch (e) { alert(e.message); }
    finally { setUpl(false); }
  };

  const handleSubmit = async () => {
    setEnreg(true);
    const c = JSON.parse(JSON.stringify(contenu || {}));
    c.page_accueil = c.page_accueil || {};
    c.page_accueil.hero = c.page_accueil.hero || {};
    c.page_accueil.hero.titre = titre.trim();
    c.page_accueil.hero.sous_titre = sousTitre.trim();
    c.page_accueil.hero.photo_url = image || '';
    c.page_accueil.message_contact = invitation.trim();
    c.page_a_propos = c.page_a_propos || {};
    c.page_a_propos.histoire = histoire.trim();
    const ph = (photos && typeof photos === 'object') ? { ...photos } : {};
    ph.hero = image || '';
    await onSauvegarder({ contenu_json: c, photos_json: ph }, '✅ Page d accueil enregistrée !');
    setEnreg(false);
  };

  return (
    <div className="edit-modal-overlay" onClick={onFermer}>
      <div className="edit-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="edit-modal-close" onClick={onFermer}>×</button>
        <div className="edit-modal-header"><h2>🏠 Page d'accueil</h2></div>
        <div className="edit-modal-body">
          <div className="edit-modal-champ">
            <label>Titre de la bannière</label>
            <input type="text" value={titre} onChange={e => setTitre(e.target.value)} placeholder="Ex: Habillez-vous avec élégance" />
          </div>
          <div className="edit-modal-champ">
            <label>Sous-titre de la bannière</label>
            <textarea rows={2} value={sousTitre} onChange={e => setSousTitre(e.target.value)} placeholder="Ex: Découvrez notre collection..." />
          </div>

          <div className="edit-modal-champ">
            <label>📸 Image de la bannière</label>
            <div className="edit-modal-photo-zone">
              {image ? (
                <div className="edit-modal-photo-preview">
                  <img src={image} alt="Bannière" />
                  <button type="button" className="edit-modal-photo-supprimer" onClick={() => setImage('')}>✕ Retirer</button>
                </div>
              ) : (
                <div className="edit-modal-photo-upload">
                  <input type="file" id="upload-hero" accept="image/*" onChange={e => handleImg(e.target.files[0])} disabled={upl} />
                  <label htmlFor="upload-hero" className="edit-modal-photo-btn">{upl ? '⏳ Upload...' : '📤 Choisir une image'}</label>
                  <p className="edit-modal-photo-info">Max 5 MB — JPG, PNG</p>
                </div>
              )}
            </div>
          </div>

          <div className="edit-modal-champ">
            <label>À propos (votre histoire)</label>
            <textarea rows={4} value={histoire} onChange={e => setHistoire(e.target.value)} placeholder="Qui êtes-vous, votre parcours, vos valeurs..." />
          </div>
          <div className="edit-modal-champ">
            <label>Message d'invitation au contact</label>
            <input type="text" value={invitation} onChange={e => setInvitation(e.target.value)} placeholder="Ex: Parlons de votre projet" />
          </div>

          <button type="button" className="edit-modal-btn-submit" disabled={enreg || upl} onClick={handleSubmit}>
            {enreg ? '⏳ Enregistrement...' : '✅ Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PANNEAU — Design (couleurs + police). Écrit design_json.
// ══════════════════════════════════════════════════════════════
function ModalDesign({ design, onSauvegarder, onFermer }) {
  const POLICES = ['poppins', 'inter', 'playfair', 'lora', 'montserrat', 'raleway', 'merriweather', 'opensans'];
  const [c1, setC1] = useState(design.couleur1 || '#005AA9');
  const [c2, setC2] = useState(design.couleur2 || '#00A651');
  const [c3, setC3] = useState(design.couleur3 || '#F5F7FA');
  const [police, setPolice] = useState(design.police || 'poppins');
  const [enreg, setEnreg] = useState(false);

  const handleSubmit = async () => {
    setEnreg(true);
    const d = { ...(design || {}), couleur1: c1, couleur2: c2, couleur3: c3, police };
    await onSauvegarder({ design_json: d }, '✅ Design enregistré !');
    setEnreg(false);
  };

  const ligneCouleur = (label, valeur, setV) => (
    <div className="edit-modal-champ">
      <label>{label}</label>
      <div className="edit-couleur-ligne">
        <input type="color" value={valeur} onChange={e => setV(e.target.value)} />
        <input type="text" value={valeur} onChange={e => setV(e.target.value)} placeholder="#000000" />
      </div>
    </div>
  );

  return (
    <div className="edit-modal-overlay" onClick={onFermer}>
      <div className="edit-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="edit-modal-close" onClick={onFermer}>×</button>
        <div className="edit-modal-header"><h2>🎨 Design</h2></div>
        <div className="edit-modal-body">
          {ligneCouleur('Couleur principale', c1, setC1)}
          {ligneCouleur('Couleur secondaire', c2, setC2)}
          {ligneCouleur('Couleur d\'accent', c3, setC3)}

          <div className="edit-modal-champ">
            <label>Police</label>
            <div className="edit-choix-groupe">
              {POLICES.map(pn => (
                <button type="button" key={pn} className={`edit-choix-btn ${police === pn ? 'actif' : ''}`} onClick={() => setPolice(pn)}>
                  {police === pn ? '✅ ' : ''}{pn}
                </button>
              ))}
            </div>
          </div>

          <button type="button" className="edit-modal-btn-submit" disabled={enreg} onClick={handleSubmit}>
            {enreg ? '⏳ Enregistrement...' : '✅ Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}